import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';
import { canTransferUnionLeadership } from '../domain/unionLeadershipTransfer.js';

const MANAGEMENT_ERROR_MESSAGES = {
  authentication_required: '로그인 세션을 확인할 수 없습니다. 다시 로그인해주세요.',
  active_union_membership_required: '활성 유니온 가입 정보가 필요합니다.',
  union_manager_required: '주딱 또는 파딱만 멤버를 제외할 수 있습니다.',
  union_leader_required: '주딱만 멤버 역할과 주딱 권한을 변경할 수 있습니다.',
  membership_not_found: '멤버 정보를 찾을 수 없습니다.',
  membership_not_active: '현재 활성 상태인 멤버가 아닙니다.',
  invalid_member_role: '지정할 수 없는 멤버 역할입니다.',
  leader_role_protected: '주딱 역할은 일반 역할 변경으로 수정하거나 제외할 수 없습니다.',
  cannot_manage_self: '본인 멤버십은 이 화면에서 변경할 수 없습니다.',
  cannot_transfer_to_self: '본인에게 주딱 권한을 이전할 수 없습니다.',
  target_already_leader: '선택한 계정이 이미 주딱입니다.',
  leadership_changed_retry: '주딱 정보가 변경되었습니다. 목록을 새로고침한 뒤 다시 시도해주세요.',
  deputy_scope_limited: '파딱은 일반 멤버만 제외할 수 있습니다.',
  union_not_active: '현재 활성 상태인 유니온이 아닙니다.',
  game_profile_union_mismatch: '현재 게임 프로필 소속이 유니온과 일치하지 않아 파딱 지정 또는 주딱 이전을 할 수 없습니다.',
};

function requireSupabaseClient() {
  if (!hasSupabaseConfig || !supabase) {
    throw new Error('Supabase 환경 변수가 설정되지 않았습니다.');
  }

  return supabase;
}

function createManagementError(error, fallbackMessage) {
  const rawMessage = String(error?.message || '');
  const matchedCode = Object.keys(MANAGEMENT_ERROR_MESSAGES).find((code) => rawMessage.includes(code));
  const managementError = new Error(
    MANAGEMENT_ERROR_MESSAGES[matchedCode] || fallbackMessage || '유니온 멤버 정보를 처리하지 못했습니다.',
  );
  managementError.code = matchedCode || error?.code || 'unknown_error';
  return managementError;
}

export async function loadUnionMemberDirectory() {
  const client = requireSupabaseClient();
  const { data, error } = await client.rpc('list_current_union_members');

  if (error) {
    throw createManagementError(
      error,
      '유니온 멤버 목록을 불러오지 못했습니다. Supabase 실행 대기 SQL을 확인해주세요.',
    );
  }

  const rows = data || [];
  const first = rows[0] || null;
  const viewerRole = first?.viewer_role || '';

  return {
    unionId: first?.union_id || '',
    unionName: first?.union_name || '',
    viewerRole,
    members: rows.map((row) => {
      const member = {
        membershipId: row.membership_id,
        nickname: row.nickname,
        avatarUrl: String(row.avatar_url || ''),
        role: row.role,
        joinedAt: row.joined_at,
        guildMatches: Boolean(row.guild_matches),
        isSelf: Boolean(row.is_self),
        canManageRoles: Boolean(row.can_manage_roles),
        canRemove: Boolean(row.can_remove),
      };

      return {
        ...member,
        canTransferLeadership: canTransferUnionLeadership({
          viewerRole,
          memberRole: member.role,
          isSelf: member.isSelf,
          guildMatches: member.guildMatches,
        }),
      };
    }),
  };
}

export async function setUnionMemberRole({ membershipId, role }) {
  const normalizedMembershipId = String(membershipId || '').trim();
  const normalizedRole = String(role || '').trim();

  if (!normalizedMembershipId) {
    throw createManagementError({ message: 'membership_not_found' });
  }

  if (!['deputy', 'member'].includes(normalizedRole)) {
    throw createManagementError({ message: 'invalid_member_role' });
  }

  const client = requireSupabaseClient();
  const { data, error } = await client.rpc('set_union_member_role', {
    target_membership_id: normalizedMembershipId,
    new_role: normalizedRole,
  });

  if (error) {
    throw createManagementError(error, '멤버 역할을 변경하지 못했습니다.');
  }

  if (data !== normalizedRole) {
    throw createManagementError(null, '멤버 역할 변경 결과를 확인하지 못했습니다.');
  }

  return data;
}

export async function transferUnionLeadership(membershipId) {
  const normalizedMembershipId = String(membershipId || '').trim();
  if (!normalizedMembershipId) {
    throw createManagementError({ message: 'membership_not_found' });
  }

  const client = requireSupabaseClient();
  const { data, error } = await client.rpc('transfer_union_leadership', {
    target_membership_id: normalizedMembershipId,
  });

  if (error) {
    throw createManagementError(error, '주딱 권한을 이전하지 못했습니다.');
  }

  if (!data?.ok || data?.new_leader_membership_id !== normalizedMembershipId) {
    throw createManagementError(null, '주딱 권한 이전 결과를 확인하지 못했습니다.');
  }

  return data;
}

export async function removeUnionMember(membershipId) {
  const normalizedMembershipId = String(membershipId || '').trim();
  if (!normalizedMembershipId) {
    throw createManagementError({ message: 'membership_not_found' });
  }

  const client = requireSupabaseClient();
  const { data, error } = await client.rpc('remove_union_member', {
    target_membership_id: normalizedMembershipId,
  });

  if (error) {
    throw createManagementError(error, '멤버를 유니온에서 제외하지 못했습니다.');
  }

  if (data !== 'removed') {
    throw createManagementError(null, '멤버 제외 결과를 확인하지 못했습니다.');
  }

  return data;
}
